/**
 * FieldEditorModal — FormModal-hosted create/edit dialog for one backlog
 * schema column: custom create/edit with a type picker + progressive-
 * disclosure SELECT options editor + Required checkbox; system rename with
 * a read-only "System · {label}" type line; a danger zone whose Archive/
 * Delete buttons render purely based on whether the caller passes the
 * corresponding callback (draft mode passes only onArchive, relabelled
 * "Remove" — see Task 5's DraftBuilder; live mode passes onArchive always
 * for non-trio fields and onDelete only for CUSTOM fields — see Task 5's
 * LiveBuilder). Purely controlled/presentational otherwise: the caller
 * supplies the initial field, receives validated values via onSubmit, and
 * owns whatever happens next (local array mutation in DRAFT, a CRUD
 * mutation in LIVE).
 */
/* eslint-disable react-refresh/only-export-components --
   this module exports the mapBacklogFieldErrorCode helper alongside the
   component so callers (the schema builder's mutation handlers) share one
   source of truth for the error-code → message mapping; losing Fast Refresh
   here is accepted (AddColumnControl precedent). */
import { useState } from 'react';
import { FormModal } from '@/components/FormModal';
import {
  labelStyle, fieldStyle, reqMarkStyle, KitCheckbox, errorTextStyle,
  btnPrimaryStyle, btnGhostStyle, btnDangerStyle, btnTonalStyle,
} from '@/components/ProjectForm';
import type { BacklogFieldOption, BacklogFieldType } from '@/types/backlogField';
import { BACKLOG_FIELD_TYPE_LABELS, type BuilderField } from './FieldBuilderRow';
import { SYSTEM_FIELD_DEFAULT_LABEL } from './AddColumnControl';

const EDITABLE_TYPES: BacklogFieldType[] = ['TEXT', 'LONG_TEXT', 'NUMBER', 'DATE', 'SELECT', 'CHECKBOX', 'MEMBER'];

/**
 * Error-code → message mapping (grounded against the real command services —
 * see this task's Interfaces section). BAD_REQUEST only ever means "SELECT
 * submitted with zero options" for payloads THIS modal builds: every other
 * BAD_REQUEST-triggering coherence rule (kind/systemRef/dataType shape) is
 * guaranteed by construction here — create is always CUSTOM, update never
 * sends kind/systemRef/dataType.
 */
export function mapBacklogFieldErrorCode(code: string | undefined): string {
  switch (code) {
    case 'FIELD_DUPLICATE':
      return 'A column with this name already exists.';
    case 'BAD_REQUEST':
      return 'Add at least one option.';
    case 'FIELD_TYPE_IMMUTABLE':
      return "This column's type can't be changed.";
    case 'FIELD_MANDATORY':
      return "This is a required column and can't be removed.";
    case 'FIELD_SYSTEM_DELETE':
      return 'System columns can be archived, not deleted.';
    case 'INVALID_ORDER':
      return 'The column order changed elsewhere — please try again.';
    default:
      return 'Could not save the column. Please try again.';
  }
}

export interface FieldEditorSubmitValues {
  label: string;
  dataType: BacklogFieldType;
  options: BacklogFieldOption[];
  required: boolean;
}

export interface FieldEditorModalProps {
  mode: 'create' | 'edit';
  /** Required when mode === 'edit'. */
  field?: BuilderField;
  /** Sibling labels (excluding the field being edited) for client-side duplicate detection. */
  existingLabels: string[];
  onClose: () => void;
  onSubmit: (values: FieldEditorSubmitValues) => void;
  onArchive?: () => void;
  archiveLabel?: string;
  archiveDescription?: string;
  onDelete?: () => void;
  isPending?: boolean;
  /** Human-readable, already mapped via mapBacklogFieldErrorCode by the caller. */
  submitError?: string | null;
}

export function FieldEditorModal({
  mode, field, existingLabels, onClose, onSubmit, onArchive,
  archiveLabel = 'Archive', archiveDescription = 'Hides this column; existing data is kept.',
  onDelete, isPending, submitError,
}: FieldEditorModalProps) {
  const isEdit = mode === 'edit';
  const isSystem = isEdit && field?.kind === 'SYSTEM';
  const [label, setLabel] = useState(field?.label ?? '');
  const [dataType, setDataType] = useState<BacklogFieldType>(field?.dataType ?? 'TEXT');
  const [options, setOptions] = useState<BacklogFieldOption[]>(field?.options ?? []);
  const [required, setRequired] = useState(field?.required ?? false);
  const [localError, setLocalError] = useState<string | null>(null);

  const trimmed = label.trim();
  const duplicateLabel = trimmed.length > 0
    && existingLabels.some((l) => l.toLowerCase() === trimmed.toLowerCase());
  const needsOptions = dataType === 'SELECT';
  const validOptions = options.filter((o) => o.value.trim().length > 0 && o.label.trim().length > 0);
  // Duplicate-label deliberately isn't part of `valid`/the disabled state: it's
  // surfaced as an inline alert on submit attempt (handleSubmit below), not a
  // silently-disabled button — matches the duplicate-label test, which clicks
  // Add and expects the alert rather than a no-op disabled click.
  const valid = trimmed.length > 0 && (!needsOptions || validOptions.length > 0);

  function addOption() {
    setOptions((prev) => [...prev, { value: '', label: '' }]);
  }
  function updateOption(i: number, patch: Partial<BacklogFieldOption>) {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  }
  function removeOption(i: number) {
    setOptions((prev) => prev.filter((_, idx) => idx !== i));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (trimmed.length === 0) { setLocalError('Give this column a name.'); return; }
    if (duplicateLabel) { setLocalError('A column with this name already exists.'); return; }
    if (needsOptions && validOptions.length === 0) { setLocalError('Add at least one option.'); return; }
    setLocalError(null);
    onSubmit({ label: trimmed, dataType, options: needsOptions ? validOptions : [], required });
  }

  function handleDelete() {
    if (!onDelete) return;
    if (!confirm("Permanently deletes this column and its value from every record. This can't be undone.")) return;
    onDelete();
  }

  const displayError = localError ?? submitError ?? null;
  const showDangerZone = isEdit && field && !field.locked && (onArchive || onDelete);

  return (
    <FormModal title={isEdit ? 'Edit column' : 'New custom field'} onClose={onClose}>
      <form onSubmit={handleSubmit} noValidate>
        <div style={{ marginBottom: 14 }}>
          <label htmlFor="bf-label" style={labelStyle}>Label<span style={reqMarkStyle}> *</span></label>
          <input
            id="bf-label"
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Risk score"
            className="focus-ring"
            style={fieldStyle}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label htmlFor="bf-type" style={labelStyle}>Type</label>
          {isEdit ? (
            <>
              <div
                id="bf-type"
                style={{
                  height: 36, display: 'flex', alignItems: 'center', padding: '0 12px',
                  borderRadius: 'var(--radius-inset)', background: 'var(--surface-inset)',
                  color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)',
                }}
              >
                {isSystem ? `System · ${SYSTEM_FIELD_DEFAULT_LABEL[field!.systemRef!]}` : BACKLOG_FIELD_TYPE_LABELS[dataType]}
              </div>
              <p style={{ margin: '6px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xs)', color: 'var(--text-subtle)' }}>
                {isSystem ? 'System columns keep their built-in type.' : "Type can't be changed after creation."}
              </p>
            </>
          ) : (
            <select
              id="bf-type"
              value={dataType}
              onChange={(e) => setDataType(e.target.value as BacklogFieldType)}
              className="focus-ring"
              style={fieldStyle}
            >
              {EDITABLE_TYPES.map((t) => (
                <option key={t} value={t}>{BACKLOG_FIELD_TYPE_LABELS[t]}</option>
              ))}
            </select>
          )}
        </div>

        {needsOptions && (
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Options<span style={reqMarkStyle}> *</span></label>
            {options.map((o, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                <input
                  aria-label={`Option ${i + 1} value`}
                  value={o.value}
                  onChange={(e) => updateOption(i, { value: e.target.value, label: o.label || e.target.value })}
                  placeholder="Value"
                  className="focus-ring"
                  style={{ ...fieldStyle, flex: 1 }}
                />
                <input
                  aria-label={`Option ${i + 1} label`}
                  value={o.label}
                  onChange={(e) => updateOption(i, { label: e.target.value })}
                  placeholder="Label"
                  className="focus-ring"
                  style={{ ...fieldStyle, flex: 1 }}
                />
                <button
                  type="button"
                  aria-label={`Remove option ${i + 1}`}
                  onClick={() => removeOption(i)}
                  className="pressable focus-ring"
                  style={{ ...btnGhostStyle, height: 36, padding: '0 10px' }}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addOption}
              className="pressable focus-ring"
              style={{ ...btnTonalStyle, height: 32, padding: '0 14px', fontSize: 'var(--text-xs)' }}
            >
              + Add option
            </button>
          </div>
        )}

        {!isSystem && (
          <label
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', marginBottom: 14,
              fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)',
            }}
          >
            <KitCheckbox checked={required} onChange={(e) => setRequired(e.target.checked)} />
            Required
          </label>
        )}

        {displayError && (
          <p role="alert" aria-live="polite" style={{ ...errorTextStyle, margin: '0 0 14px' }}>
            {displayError}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="pressable focus-ring" style={btnGhostStyle}>
            Cancel
          </button>
          <button type="submit" disabled={isPending || !valid} className="pressable focus-ring" style={{ ...btnPrimaryStyle, opacity: isPending || !valid ? 0.4 : 1 }}>
            {isPending ? 'Saving…' : isEdit ? 'Save' : 'Add'}
          </button>
        </div>
      </form>

      {showDangerZone && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {onArchive && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--text-subtle)' }}>
                {archiveDescription}
              </span>
              <button type="button" onClick={onArchive} className="pressable focus-ring" style={btnTonalStyle}>
                {archiveLabel}
              </button>
            </div>
          )}
          {onDelete && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--text-subtle)' }}>
                Permanently removes this column and its data.
              </span>
              <button type="button" onClick={handleDelete} className="pressable focus-ring" style={btnDangerStyle}>
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </FormModal>
  );
}
